import React, { useEffect, useState } from 'react';

import { CharacterResourceWithUsed, Character, CharacterClass, SpellcastingCustomizations, Spell, SpellSlotType } from "@dndModels";

import FlexBox from '@components/layout/FlexBox';
import CommonUtilities from '@utilities/common_utilities';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import DataUtilities from '@dndUtilities/data_utilities';
import { ABILITY_SCORES, CHARACTER_LEVELS, DICE_SIZE_OPTIONS, LEVELED_SPELL_LEVELS_STRINGS, ONES_0_to_50, SPELL_LEVELS_USING_TYPE } from '@dndModels/Constants';
import InfoBox from '@components/input/InfoBox';
import { AbilityScoreType, SpellLevel } from '@dndModels/Types';
import DisplayObjects from '@dndComponents/display/DisplayObjects';
import SelectObjectBox from '@components/input/selects/SelectObjectBox';
import SelectSpellIndex from '@dndComponents/select/SelectSpellIndex';

interface Props {
  char: Character;
}

const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterSpellcastingCustomizations({
  char,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);

  const customizations = char.customizations;

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const renderSpellcastingCustomization = (char_class: CharacterClass) => {
    // debugger;
    const spellcasting = char_class.spellcasting;
    const sc_custom = customizations.spellcasting.find(o => o.source_true_id === char_class.true_id);
    
    const updateSCCustom = async (updates?: Partial<SpellcastingCustomizations>) => {
      let sc_custom2 = customizations.spellcasting.find(o => o.source_true_id === char_class.true_id);
      if (!sc_custom2) {
        sc_custom2 = new SpellcastingCustomizations();
        sc_custom2.source_true_id = char_class.true_id;
        customizations.spellcasting.push(sc_custom2);
      }
      Object.assign(sc_custom2, updates);
      let keys = Object.keys(sc_custom2);
      for (const key of keys) {
        const value = (sc_custom2 as any)[key];
        if (value === undefined) {
          delete (sc_custom2 as any)[key];
        }
      }
      keys = Object.keys(sc_custom2);
      if (keys.length === 1) { // Just source_true_id
        customizations.spellcasting = customizations.spellcasting.filter(o => o.source_true_id !== char_class.true_id);
      }
      setPing(!ping);
      char.changed();
      char.subscription_holder.recalculate();
    };

    return (
      <FlexBox key={char_class.true_id} direction="column">
        <FlexBox><hr /></FlexBox>
        <FlexBox className="header centered">{ char_class.name }</FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spellcasting Ability</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spellcasting_ability }</FlexBox>
              { sc_custom && sc_custom.spellcasting_ability &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ABILITY_SCORES]}
              value={ sc_custom && sc_custom.spellcasting_ability ? sc_custom.spellcasting_ability : "Don't Override" }
              onChange={(changed: "Don't Override" | AbilityScoreType) => {
                // debugger;
                updateSCCustom({ spellcasting_ability: changed === "Don't Override" ? undefined : changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spellcasting Modifier</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spellcasting_modifier }</FlexBox>
              { sc_custom && sc_custom.spellcasting_mod &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.spellcasting_mod ? `${sc_custom.spellcasting_mod}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ spellcasting_mod: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spell Attack Modifier</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spell_attack }</FlexBox>
              { sc_custom && sc_custom.spell_attack &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.spell_attack ? `${sc_custom.spell_attack}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ spell_attack: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spell Save DC</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spell_dc }</FlexBox>
              { sc_custom && sc_custom.spell_save_dc &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.spell_save_dc ? `${sc_custom.spell_save_dc}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ spell_save_dc: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Max Spell Level</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spell_level_max }</FlexBox>
              { sc_custom && sc_custom.max_spell_level &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.max_spell_level ? `${sc_custom.max_spell_level}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ max_spell_level: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Max Cantrips Known</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.cantrips_max }</FlexBox>
              { sc_custom && sc_custom.max_cantrips_known &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.max_cantrips_known ? `${sc_custom.max_cantrips_known}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ max_cantrips_known: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Max Spells Prepared</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spells_prepared_max }</FlexBox>
              { sc_custom && sc_custom.max_spells_prepared &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.max_spells_prepared ? `${sc_custom.max_spells_prepared}` : "Don't Override" }
              onChange={(changed: string) => {
                // debugger;
                updateSCCustom({ max_spells_prepared: changed === "Don't Override" ? undefined : +changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spellcasting Type</FlexBox>
              <FlexBox className="small_label gray">
                <DisplayObjects type="spell_slot_type" ids={ [spellcasting.spell_table] } />
              </FlexBox>
              { sc_custom && sc_custom.spellcasting_type_id &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectObjectBox
              name="Override"
              data_type="spell_slot_type"
              extra_options={["Don't Override"]}
              value={ sc_custom && sc_custom.spellcasting_type_id ? sc_custom.spellcasting_type_id : "Don't Override" }
              onChange={(changed: string) => {
                updateSCCustom({ spellcasting_type_id: changed === "Don't Override" ? undefined : changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Full/Half/Third</FlexBox>
              <FlexBox className="small_label gray">{ spellcasting.spellcasting_level }</FlexBox>
              { sc_custom && sc_custom.spellcasting_level &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectStringBox
              name="Override"
              options={["Don't Override",...ONES_0_to_50]}
              value={ sc_custom && sc_custom.spellcasting_level ? `${sc_custom.spellcasting_level}` : "Don't Override" }
              onChange={(changed: "Full" | "Half" | "Third" | "Don't Override") => {
                // debugger;
                updateSCCustom({ spellcasting_level: changed === "Don't Override" ? undefined : changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "50%"}}>
            <FlexBox direction="column">
              <FlexBox>Spell List</FlexBox>
              <FlexBox className="small_label gray">
                <DisplayObjects type="spell_list" ids={ spellcasting.spell_list_ids } />
              </FlexBox>
              { sc_custom && sc_custom.spell_list_id &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <SelectObjectBox
              name="Override"
              data_type="spell_list"
              extra_options={["Don't Override"]}
              value={ sc_custom && sc_custom.spell_list_id ? sc_custom.spell_list_id : "Don't Override" }
              onChange={(changed: string) => {
                updateSCCustom({ spell_list_id: changed === "Don't Override" ? undefined : changed });
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <SelectSpellIndex
            spell_ids={sc_custom && sc_custom.bonus_spells ? sc_custom.bonus_spells : []} 
            onChange={(changed: string[]) => {
              updateSCCustom({ bonus_spells: changed.length === 0 ? undefined : changed });
            }} 
          />
        </FlexBox>
      </FlexBox>
    );
  }

  const renderSpellSlotLevelProgress = (spell_slot_type: SpellSlotType) => {
    const slot_custom = customizations.spell_slot_level_progress[spell_slot_type._id];
    
    const updateSlotCustom = async ({ override, modifier }: { override?: string, modifier?: string }) => {
      const slot_custom2 = customizations.spell_slot_level_progress[spell_slot_type._id];
      if (slot_custom2) {
        if (override) {
          if (override === "Don't Override") {
            if (slot_custom2.modifier === undefined) {
              delete customizations.spell_slot_level_progress[spell_slot_type._id];
            } else {
              delete slot_custom2.override;
            }
          } else {
            slot_custom2.override = +override;
          }
        } else if (modifier) {
          if (modifier === "Don't Override") {
            if (slot_custom2.override === undefined) {
              delete customizations.spell_slot_level_progress[spell_slot_type._id];
            } else {
              delete slot_custom2.modifier;
            }
          } else {
            slot_custom2.modifier = +modifier;
          }
        }
      } else {
        if (override) {
          if (override !== "Don't Override") {
            customizations.spell_slot_level_progress[spell_slot_type._id] = { override: +override };
          }
        } else if (modifier) {
          if (modifier !== "Don't Override") {
            customizations.spell_slot_level_progress[spell_slot_type._id] = { modifier: +modifier };
          }
        }
      }
      setPing(!ping);
      char.changed();
      char.subscription_holder.recalculate();
    };

    return (
      <FlexBox direction="row" key={spell_slot_type._id}>
        <FlexBox xs_expand={0} style={{ width: "50%"}}>
          <FlexBox direction="column">
            <FlexBox>{ spell_slot_type.name }</FlexBox>
            <FlexBox className="small_label gray">{ char.mind.spell_slot_level_progress[spell_slot_type._id] }</FlexBox>
            { slot_custom !== undefined &&
              <FlexBox className="small_label gray">Overridden</FlexBox>
            }
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <SelectStringBox
            name="Override"
            options={["Don't Override",...CHARACTER_LEVELS]}
            value={ slot_custom && slot_custom.override !== undefined ? `${slot_custom.override}` : "Don't Override" }
            onChange={(changed: string) => {
              // debugger;
              updateSlotCustom({ override: changed });
            }}
          />
        </FlexBox>
      </FlexBox>
    );
  }

  const renderSpellSlots = (spell_slot_type: SpellSlotType) => {
    const slot_custom = customizations.slots[spell_slot_type._id];
    
    const updateSlotCustom = async (level: number, { override, modifier }: { override?: string, modifier?: string }) => {
      const slot_custom2 = customizations.slots[spell_slot_type._id];
      if (slot_custom2) {
        const level_custom2 = slot_custom2[level];
        if (level_custom2) {
          if (override) {
            if (override === "Don't Override") {
              if (level_custom2.modifier === undefined) {
                delete slot_custom2[level];
              } else {
                delete level_custom2.override;
              }
            } else {
              level_custom2.override = +override;
            }
          } else if (modifier) {
            if (modifier === "Don't Override") {
              if (level_custom2.override === undefined) {
                delete slot_custom2[level];
              } else {
                delete level_custom2.modifier;
              }
            } else {
              level_custom2.modifier = +modifier;
            }
          }
          if (Object.keys(level_custom2).length === 0) {
            delete customizations.slots[spell_slot_type._id][level];
          }
        } else {
          if (override) {
            if (override !== "Don't Override") {
              slot_custom2[level] = { override: +override };
            }
          } else if (modifier) {
            if (modifier !== "Don't Override") {
              slot_custom2[level] = { modifier: +modifier };
            }
          }
        }
        if (Object.keys(slot_custom2).length === 0) {
          delete customizations.slots[spell_slot_type._id];
        }
      } else {
        if (override) {
          if (override !== "Don't Override") {
            customizations.slots[spell_slot_type._id] = {};
            customizations.slots[spell_slot_type._id][level] = { override: +override };
          }
        } else if (modifier) {
          if (modifier !== "Don't Override") {
            customizations.slots[spell_slot_type._id] = {};
            customizations.slots[spell_slot_type._id][level] = { modifier: +modifier };
          }
        }
      }
      setPing(!ping);
      char.changed();
      char.subscription_holder.recalculate();
    };

    const renderSlotsForLevel = (level: SpellLevel) => {
      const level_custom = slot_custom ? slot_custom[level] : undefined;
      return (
        <FlexBox key={level} className="padded">
          <FlexBox direction="row">
            <FlexBox>
              <SelectStringBox 
                name={ `Level ${level} `}
                options={LEVELED_SPELL_LEVELS_STRINGS}
                extra_options={["Don't Override", "0"]}
                value={level_custom && level_custom.override !== undefined ? `${level_custom.override}` : "Don't Override"}
                onChange={(changed: string) => {
                  updateSlotCustom(level, { override: changed });
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      );
    }

    return (
      <FlexBox direction="column" key={spell_slot_type._id}>
        <FlexBox><hr /></FlexBox>
        <FlexBox xs_expand={0} className="centered">
          <FlexBox direction="column">
            <FlexBox>{ spell_slot_type.name }</FlexBox>
            { slot_custom !== undefined &&
              <FlexBox className="small_label gray">Overridden</FlexBox>
            }
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <FlexBox direction="column">
            { SPELL_LEVELS_USING_TYPE.filter(o => o > 0).map(level => renderSlotsForLevel(level))}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  }

  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      <FlexBox>
        <InfoBox label="Spellcasting Customizations" info_name="Spellcasting Customizations">
          <>
            <p>
              Spellcasting is a complicated feature with a lot to it, so the customizations for it have to be complicated too.
              We have Info Boxes like this one for each customizable element of your spellcasting. 
            </p>
            <p>
              Spellcasting features are always tied to a class, so you will have to select a class that your character has 
              to work with the specifics of its spellcasting feature.
            </p>
            <p>
              Sort of separate from your spellcasting features is your Spell Slots, and your Spell Slot Level Progress. 
              They can be customized separate from your class.
            </p>
            <p>
              This will be moved to the Spell Slots InfoBox.

              Warlocks use their own Spell Slot Level Progress, 
              and have their own separate spell slots (commonly called Pact Slots) 
              which are kept separate from regular spell slots from other classes. 
              We provide the ability to see and customize your current Spell Slot Level Progress 
              with both regular and Pact Slots. 
              If you customize your Spell Slot Level Progress for either type, 
              it will effect the number of slots of each level you have of that type to 
              fit the corresponding table at the level that your customizations put it at.

              Both types have a table used to determine your slots. 
              By default, if you are playing as a full caster class that uses that type of spellcasting, and are not multiclassing,
              then your Spell Slot Level Progress will be equal to your Character Level. 
              If all of that is the same but you are instead a half caster class, then it will half your Character Level (rounding up),
              and if you're playing as a class that doesn't have spellcasting, but take a subclass that gives spellcasting,
              then it will be one third of your Character Level (rounding up).

              If you are multiclassing then you take the levels of each class for each type of spellcasting 
              and take the level of progress you get from each class that uses the same type, and total their progress 
              to determine your spell slots for that type.
              For example if you have 1 level with a full caster class and 2 levels with a half caster, 
              that puts you at 2 levels of progress for that type of spellcasting, 
              so you would have the same spell slots as a 2nd level full caster with no other levels.

              One of the options in the class specific options is to set your spellcasting for it as Full, Half, or Third, 
              making it so you can see and customize how much that class contributes to your spell slot progress.

              If you override your spell slot level progress here, 
              the system will ignore everything from your classes for determining your spell slots of that type, and will 
              instead use the level set here to determine them.

              If you customize your number of slots for a specific level for a slot type, then that change will be applied
              after the slots from your spell slot level progress. 
            </p>
          </>
        </InfoBox>
      </FlexBox>
      { char.classes.length === 0 ? 
        <>Spellcasting Customization requires your character has at least one class.</> 
      :
        <>
          <FlexBox><hr /></FlexBox>
          <InfoBox label="Total Spell Slot Level Progress" info_name="Spell Slot Level Progress">
            <>
              <p>
                Warlocks use their own Spell Slot Level Progress, 
                and have their own separate spell slots (commonly called Pact Slots) 
                which are kept separate from regular spell slots from other classes. 
                We provide the ability to see and customize your current Spell Slot Level Progress 
                with both regular and Pact Slots. 
                If you customize your Spell Slot Level Progress for either type, 
                it will effect the number of slots of each level you have of that type to 
                fit the corresponding table at the level that your customizations put it at.
              </p>
              <p>
                Both types have a table used to determine your slots. 
                By default, if you are playing as a full caster class that uses that type of spellcasting, and are not multiclassing,
                then your Spell Slot Level Progress will be equal to your Character Level. 
                If all of that is the same but you are instead a half caster class, then it will half your Character Level (rounding up),
                and if you're playing as a class that doesn't have spellcasting, but take a subclass that gives spellcasting,
                then it will be one third of your Character Level (rounding up).
              </p>
              <p>
                If you are multiclassing then you take the levels of each class for each type of spellcasting 
                and take the level of progress you get from each class that uses the same type, and total their progress 
                to determine your spell slots for that type.
                For example if you have 1 level with a full caster class and 2 levels with a half caster, 
                that puts you at 2 levels of progress for that type of spellcasting, 
                so you would have the same spell slots as a 2nd level full caster with no other levels.
              </p>
              <p>
                One of the options in the class specific options is to set your spellcasting for it as Full, Half, or Third, 
                making it so you can see and customize how much that class contributes to your spell slot progress.
              </p>
              <p>
                If you override your spell slot level progress here, 
                the system will ignore everything from your classes for determining your spell slots of that type, and will 
                instead use the level set here to determine them.
              </p>
              <p>
                If you customize your number of slots for a specific level for a slot type, then that change will be applied
                after the slots from your spell slot level progress. 
              </p>
            </>
          </InfoBox>
          { data_util.spell_slot_types.map(spell_slot_type => renderSpellSlotLevelProgress(spell_slot_type)) }
          <FlexBox><hr /></FlexBox>
          <InfoBox label="Spell Slots by type for each level." info_name="Spell Slots by Type">
            <>
              <p>
                Here we allow you to customize exactly how many spell slots of each type you have of each level
              </p>
            </>
          </InfoBox>
          { data_util.spell_slot_types.map(spell_slot_type => renderSpellSlots(spell_slot_type)) }
          <FlexBox></FlexBox>
          { char.classes.map(char_class => renderSpellcastingCustomization(char_class)) }
        </>
      }
    </FlexBox>
  );
}

